/**
 * Client Accommodation Management Component
 * Allows clients to view and manage their accommodation bookings
 */

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { accommodationService } from '../../services/accommodationService';
import { AccommodationBookingRequest, BookingRequestStatus } from '../../types/accommodation';
import {
  BuildingOfficeIcon,
  MapPinIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

const AccommodationManagement: React.FC = () => {
  const { actualTheme } = useTheme();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [bookings, setBookings] = useState<AccommodationBookingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState<AccommodationBookingRequest | null>(null);
  const [filterStatus, setFilterStatus] = useState<BookingRequestStatus | 'all'>('all');

  useEffect(() => {
    if (isAuthenticated && user) {
      loadBookings();
    }
  }, [isAuthenticated, user]);

  const loadBookings = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const clientBookings = await accommodationService.getBookingRequestsByClient(user.id);
      setBookings(clientBookings);
    } catch (error) {
      console.error('Error loading bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: BookingRequestStatus) => {
    const statusConfig: Record<BookingRequestStatus, { bg: string; text: string; icon: any }> = {
      draft: { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-300', icon: ClockIcon },
      pending: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', icon: ClockIcon },
      sent: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', icon: ClockIcon },
      acknowledged: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', icon: CheckCircleIcon },
      quoted: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', icon: ClockIcon },
      awaiting_response: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', icon: ClockIcon },
      partial_confirmed: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-300', icon: ExclamationTriangleIcon },
      confirmed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', icon: CheckCircleIcon },
      declined: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', icon: XCircleIcon },
      expired: { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-300', icon: ClockIcon },
      rejected: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', icon: XCircleIcon },
      alternative_suggested: { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-300', icon: ExclamationTriangleIcon },
      cancelled: { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-300', icon: XCircleIcon },
      cancellation_requested: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', icon: ClockIcon },
      payment_pending: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', icon: ClockIcon },
      payment_failed: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', icon: XCircleIcon },
      completed: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', icon: CheckCircleIcon },
      failed: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', icon: XCircleIcon },
      manual_review: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', icon: ExclamationTriangleIcon }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-3 w-3" />
        {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
      </span>
    );
  };

  const filteredBookings = filterStatus === 'all'
    ? bookings
    : bookings.filter(b => b.status === filterStatus);

  if (!isAuthenticated || !user) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${
        actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
      }`}>
        <div className={`text-center rounded-2xl shadow-lg p-8 max-w-md ${
          actualTheme === 'dark'
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white border border-neutral-200'
        }`}>
          <ExclamationTriangleIcon className={`h-16 w-16 mx-auto mb-4 ${
            actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-400'
          }`} />
          <h2 className={`text-2xl font-bold mb-2 ${
            actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
          }`}>
            Authentication Required
          </h2>
          <p className={`mb-6 ${
            actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
          }`}>
            Please sign in to view your accommodation bookings
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-4 sm:p-6 lg:p-8 ${
      actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'
    }`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`rounded-2xl shadow-lg p-6 mb-6 ${
          actualTheme === 'dark'
            ? 'bg-gradient-to-br from-slate-800/90 to-slate-700/90 border border-slate-600/30'
            : 'bg-white/90 border border-neutral-200/50'
        }`}>
          <div className="flex items-center gap-4 mb-4">
            <div className={`p-3 rounded-lg ${
              actualTheme === 'dark' ? 'bg-primary-700/20' : 'bg-primary-100'
            }`}>
              <BuildingOfficeIcon className={`w-8 h-8 ${
                actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
              }`} />
            </div>
            <div className="flex-1">
              <h1 className={`text-3xl font-bold mb-2 ${
                actualTheme === 'dark' ? 'text-slate-200' : 'text-gray-900'
              }`}>
                My Accommodations
              </h1>
              <p className={`text-sm ${
                actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
              }`}>
                Manage your accommodation bookings for your safaris
              </p>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className={`rounded-lg border p-4 mb-6 ${
          actualTheme === 'dark'
            ? 'bg-slate-800/50 border-slate-700'
            : 'bg-white border-neutral-200'
        }`}>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filterStatus === 'all'
                  ? actualTheme === 'dark'
                    ? 'bg-primary-600 text-white'
                    : 'bg-primary-600 text-white'
                  : actualTheme === 'dark'
                  ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              All ({bookings.length})
            </button>
            {(['confirmed', 'awaiting_response', 'partial_confirmed', 'rejected', 'cancelled'] as BookingRequestStatus[]).map(status => {
              const count = bookings.filter(b => b.status === status).length;
              if (count === 0) return null;
              return (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    filterStatus === status
                      ? actualTheme === 'dark'
                        ? 'bg-primary-600 text-white'
                        : 'bg-primary-600 text-white'
                      : actualTheme === 'dark'
                      ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-4"></div>
              <p className={`${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
                Loading your bookings...
              </p>
            </div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className={`text-center py-12 rounded-lg ${
            actualTheme === 'dark' ? 'bg-slate-800/50' : 'bg-white'
          }`}>
            <BuildingOfficeIcon className={`h-16 w-16 mx-auto mb-4 ${
              actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-400'
            }`} />
            <p className={`text-lg font-medium mb-2 ${
              actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
            }`}>
              No accommodation bookings found
            </p>
            <p className={`text-sm ${
              actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
            }`}>
              {filterStatus === 'all'
                ? 'Your accommodation bookings will appear here once you book a safari'
                : `No bookings with status "${filterStatus}"`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <div
                key={booking.id}
                onClick={() => setSelectedBooking(selectedBooking?.id === booking.id ? null : booking)}
                className={`rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg ${
                  actualTheme === 'dark'
                    ? 'bg-slate-800 border-slate-700 hover:border-primary-600'
                    : 'bg-white border-neutral-200 hover:border-primary-300'
                } overflow-hidden`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <MapPinIcon className={`w-5 h-5 ${
                          actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                        }`} />
                        <h3 className={`text-xl font-bold ${
                          actualTheme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {booking.location}
                        </h3>
                        {getStatusBadge(booking.status)}
                      </div>
                      <div className={`text-sm ${
                        actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                      }`}>
                        {booking.groupName}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className={`w-4 h-4 ${
                        actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                      }`} />
                      <div>
                        <div className={`text-xs ${
                          actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-500'
                        }`}>
                          Arrival
                        </div>
                        <div className={`font-medium ${
                          actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                        }`}>
                          {new Date(booking.arrivalDate).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <CalendarIcon className={`w-4 h-4 ${
                        actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                      }`} />
                      <div>
                        <div className={`text-xs ${
                          actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-500'
                        }`}>
                          Departure
                        </div>
                        <div className={`font-medium ${
                          actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                        }`}>
                          {new Date(booking.departureDate).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <BuildingOfficeIcon className={`w-4 h-4 ${
                        actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                      }`} />
                      <div>
                        <div className={`text-xs ${
                          actualTheme === 'dark' ? 'text-slate-500' : 'text-neutral-500'
                        }`}>
                          Guests
                        </div>
                        <div className={`font-medium ${
                          actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                        }`}>
                          {booking.guestCount} guest{booking.guestCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  </div>

                  {booking.confirmationNumber && (
                    <div className={`p-3 rounded-lg mb-4 ${
                      actualTheme === 'dark' ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
                    }`}>
                      <div className="flex items-center gap-2">
                        <CheckCircleIcon className={`w-5 h-5 ${
                          actualTheme === 'dark' ? 'text-green-400' : 'text-green-600'
                        }`} />
                        <div>
                          <div className={`text-xs font-medium ${
                            actualTheme === 'dark' ? 'text-green-300' : 'text-green-700'
                          }`}>
                            Confirmation Number
                          </div>
                          <div className={`font-mono text-sm ${
                            actualTheme === 'dark' ? 'text-green-200' : 'text-green-800'
                          }`}>
                            {booking.confirmationNumber}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedBooking?.id === booking.id && (
                    <div className={`mt-4 pt-4 border-t ${
                      actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
                    }`}>
                      <div className="space-y-3">
                        <div>
                          <div className={`text-sm font-medium mb-1 ${
                            actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                          }`}>
                            Room Type
                          </div>
                          <div className={`text-sm ${
                            actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                          }`}>
                            {booking.roomType || 'Standard'}
                          </div>
                        </div>
                        {booking.mealPlan && (
                          <div>
                            <div className={`text-sm font-medium mb-1 ${
                              actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                            }`}>
                              Meal Plan
                            </div>
                            <div className={`text-sm ${
                              actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                            }`}>
                              {booking.mealPlan === 'breakfast' ? 'Breakfast'
                                : booking.mealPlan === 'halfBoard' ? 'Half Board'
                                : booking.mealPlan === 'fullBoard' ? 'Full Board'
                                : 'No meals'}
                            </div>
                          </div>
                        )}
                        {booking.availabilityResponse && (
                          <div>
                            <div className={`text-sm font-medium mb-1 ${
                              actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                            }`}>
                              Provider Response
                            </div>
                            <div className={`text-sm ${
                              actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                            }`}>
                              {booking.availabilityResponse.message || 'Confirmed'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccommodationManagement;

